import {
  EDITABLE_GAP_FIELDS,
  GapDraftEditableKind,
  isFinalGapValue,
  type EditGapDraftChangeApiRequest,
  type GapDraftEditValue,
  type GapDraftView,
} from '@momentum/types';

/**
 * The edit form's data rules: which fields a given draft actually offers, what
 * they start at, and what a pass over them amounts to.
 *
 * All of it is kept out of the components because a change request is a
 * contract, not a form submission — the server re-validates every path, and the
 * two have to agree about which paths exist on this document at all.
 */

/** One concrete field the form renders, with the list index already resolved. */
export interface EditableGapField {
  /** Dotted path from the document root, e.g. `gap.actionItems.items.1.text`. */
  path: string;
  label: string;
  kind: GapDraftEditableKind;
  /** The heading the field sits under, matching the reader's own sections. */
  group: string;
}

/** A field's state in the open form. */
export interface EditableGapFieldState extends EditableGapField {
  /** What the control shows. Always a string — a score is parsed on save. */
  value: string;
  /** What the agents left, as the form seeded it. */
  seedValue: string;
  /** Why this change was made, optional and per change. */
  reason: string;
  /** Whether the reviewer has moved this field away from its seed. */
  isDirty: boolean;
  /** Whether a human had already edited this path before this pass. */
  wasEdited: boolean;
  /** Set when the typed value cannot be sent, which blocks the save. */
  problem?: string;
}

/**
 * The reader's section names, keyed by the `gap.` segment the path starts with,
 * so the form walks in the same order as the document it is editing.
 */
const GROUP_OF_FIELD: Record<string, string> = {
  title: 'The gap',
  severity: 'Scores',
  missability: 'Scores',
  potentialImpact: 'Impact & actions',
  roiImpact: 'Impact & actions',
  actionItems: 'Impact & actions',
  projectOverview: 'Overview',
  escalation: 'How it escalated',
  seenBefore: 'Seen before',
  fullProjectContext: 'Full project context',
};

const WILDCARD_SEGMENT = '*';

/** Reads a dotted path off the draft, without knowing what shape sits there. */
export function valueAtPath(draft: GapDraftView, path: string): unknown {
  let cursor: unknown = draft;
  for (const segment of path.split('.')) {
    if (cursor === null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor;
}

/**
 * Every FINAL field of THIS draft: the allow-listed paths that hold a value on
 * `gap`, with each list path expanded over the items the draft actually holds.
 *
 * A path the pipeline left empty is not offered. There is no version of it to
 * correct, so an editable box there would be authoring — and the form would be
 * asking the reviewer to fill in fields (`roiImpact`, most of the time) that the
 * page itself draws as holes. The server refuses the same paths, and the two
 * have to agree.
 *
 * A list nobody wrote likewise contributes nothing, for a second reason: `$set`
 * on an index past the end of an array pads it with nulls. Adding an action item
 * is a different feature.
 */
export function expandEditableFields(draft: GapDraftView): EditableGapField[] {
  return EDITABLE_GAP_FIELDS.flatMap((editableField) => {
    const group = GROUP_OF_FIELD[editableField.path.split('.')[1] ?? ''] ?? '';
    const wildcardAt = editableField.path.indexOf(`.${WILDCARD_SEGMENT}.`);

    if (wildcardAt === -1) {
      return isFinalGapValue(valueAtPath(draft, editableField.path))
        ? [{ ...editableField, group }]
        : [];
    }

    const listPath = editableField.path.slice(0, wildcardAt);
    const list = valueAtPath(draft, listPath);
    if (!Array.isArray(list)) return [];

    return list.flatMap((_item, itemIndex) => {
      const path = editableField.path.replace(
        WILDCARD_SEGMENT,
        String(itemIndex)
      );
      if (!isFinalGapValue(valueAtPath(draft, path))) return [];

      return [
        {
          path,
          label: `${editableField.label} ${itemIndex + 1}`,
          kind: editableField.kind,
          group,
        },
      ];
    });
  });
}

/**
 * What the form starts at: the `gap` layer alone.
 *
 * Never the reader's run-layer fallback, even though that is what the page
 * shows where `gap` is unwritten. Seeding from the run would make an untouched
 * save copy the finder's raw prose into the product layer and record it as a
 * human edit — the reviewer would be credited with words they never wrote. In
 * practice the form only offers final fields, so the empty case is unreachable
 * from it; the rule stays stated here because it is the reason this reads `gap`
 * rather than what the page renders.
 */
export function seedValueOf(draft: GapDraftView, path: string): string {
  const value = valueAtPath(draft, path);
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

/** Whether a human has already edited this exact path, or anything under it. */
export function isEditedPath(draft: GapDraftView, path: string): boolean {
  return draft.editedPaths.some(
    (editedPath) => editedPath === path || editedPath.startsWith(`${path}.`)
  );
}

/**
 * What a typed value cannot express, or nothing when it can.
 *
 * A score is the only field with a shape to get wrong, and an emptied one is a
 * refusal rather than an error: the API takes a number for a score path, so
 * there is no value that means "unset it". Clearing the box is read as leaving
 * the field alone, and `toEditChanges` drops it.
 */
export function problemWith(
  field: EditableGapField,
  value: string
): string | undefined {
  if (field.kind !== GapDraftEditableKind.SCORE) return undefined;
  if (value.trim().length === 0) return undefined;

  const score = Number(value);
  if (!Number.isFinite(score)) return 'Not a number.';
  if (score < 0 || score > 1) return 'Scores run from 0 to 1.';
  return undefined;
}

/** The value a field sends, or undefined when it has nothing to send. */
function toSentValue(
  field: EditableGapField,
  value: string
): GapDraftEditValue | undefined {
  if (field.kind !== GapDraftEditableKind.SCORE) return value;
  return value.trim().length === 0 ? undefined : Number(value);
}

/**
 * One pass over the form as the request the server takes.
 *
 * Only what moved: a field the reviewer never touched carries no change, so a
 * save that fixed one sentence records one sentence and the history stays
 * readable as a list of decisions rather than of form submissions.
 */
export function toEditChanges(
  fields: EditableGapFieldState[]
): EditGapDraftChangeApiRequest[] {
  return fields.flatMap((field) => {
    if (!field.isDirty || field.problem !== undefined) return [];

    const value = toSentValue(field, field.value);
    if (value === undefined) return [];

    const reason = field.reason.trim();
    return [{ path: field.path, value, ...(reason ? { reason } : {}) }];
  });
}

/** A stored edit value as the history renders it. */
export function formatEditValue(value: GapDraftEditValue): string {
  return typeof value === 'number' ? value.toFixed(2) : value;
}
